/**
 * Acuity's gear-bot mascot.
 *
 * Ported from the original student app (web/src/student/MascotBot.tsx). The
 * artwork is the same twelve transparent PNGs, copied into public/mascot — same
 * pose names, same state mapping, so any surface written against the original's
 * API works here unchanged.
 *
 * One difference: the original used framer-motion for the idle bob. That is a
 * ~100KB dependency for a single vertical translate, so the bob is a CSS
 * keyframe here instead (see .student-mascot in student.css). It looks the
 * same, respects prefers-reduced-motion for free, and keeps the bundle down.
 *
 * If a rigged Rive character is ever commissioned, the original's RiveMascot
 * wrapper drops in around this component without touching callers.
 */

export const MASCOT_POSES = {
  idle: "/mascot/idle.png",
  waving: "/mascot/waving.png",
  reading: "/mascot/reading.png",
  thinking: "/mascot/thinking.png",
  book: "/mascot/holding-book.png",
  laptop: "/mascot/holding-laptop.png",
  celebrating: "/mascot/celebrating.png",
  pointing: "/mascot/pointing.png",
  confused: "/mascot/confused.png",
  sleeping: "/mascot/sleeping.png",
  graduation: "/mascot/graduation.png",
  trophy: "/mascot/trophy.png",
} as const;

export type MascotPose = keyof typeof MASCOT_POSES;
export type MascotState = "idle" | "happy" | "thinking" | "celebrate";

/** Legacy state names from the original's Mascot API → poses. */
const STATE_TO_POSE: Record<MascotState, MascotPose> = {
  idle: "idle",
  happy: "waving",
  thinking: "thinking",
  celebrate: "celebrating",
};

/** Poses that drift gently; the rest sit still. */
const BOBBING = new Set<MascotPose>(["idle", "thinking", "reading"]);
const CHEERING = new Set<MascotPose>(["celebrating", "trophy", "graduation"]);

export function Mascot({
  state = "idle",
  pose,
  size = 96,
}: {
  state?: MascotState;
  pose?: MascotPose;
  size?: number;
}) {
  const p = pose || STATE_TO_POSE[state] || "idle";
  const motion = CHEERING.has(p) ? " is-cheering" : BOBBING.has(p) ? " is-bobbing" : "";

  return (
    <img
      className={`student-mascot${motion}`}
      src={MASCOT_POSES[p]}
      width={size}
      height={size}
      alt=""
      aria-hidden
      draggable={false}
      loading="lazy"
    />
  );
}
